import React from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Paper,
  InputBase,
  Avatar,
  type PaperProps,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLoggedInProviderId } from '../../hooks/useLoggedInProviderId';
import { useCanOnboardUser } from '../../hooks/permissions/useCanOnboardUser';
import authService from '../../services/authService';
import { useIsOnboardingUser } from '../../hooks/useIsOnboardingUser';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import * as providerSlice from '../../data/provider.slice';
import { ExpandMore, Search } from '@mui/icons-material';

const PREFIX = 'HomeAppBar';

const classes = {
  container: `${PREFIX}-container`,
  appName: `${PREFIX}-appName`,
  logo: `${PREFIX}-logo`,
  userInfo: `${PREFIX}-userInfo`,
  toolbar: `${PREFIX}-toolbar`,
  links: `${PREFIX}-links`,
  link: `${PREFIX}-link`,
  selectedLink: `${PREFIX}-selectedLink`,
  icon: `${PREFIX}-icon`,
  root: `${PREFIX}-root`,
  menuButton: `${PREFIX}-menuButton`,
  title: `${PREFIX}-title`,
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  [`&.${classes.container}`]: {
    borderRadius: '2px',
  },

  [`& .${classes.appName}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.logo}`]: {
    width: 134,
  },

  [`& .${classes.userInfo}`]: {
    margin: theme.spacing(2),
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(1, 16, 18, 0.9)',
    color: '#fff',
  },

  [`& .${classes.links}`]: {
    flexGrow: 1,
    display: 'flex',
  },

  [`& .${classes.link}`]: {
    display: 'flex',
    alignItems: 'flex-end',
    textDecoration: 'none',
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },

  [`& .${classes.selectedLink}`]: {
    display: 'flex',
    alignItems: 'flex-end',
    textDecoration: 'none',
    color: theme.palette.primary.light,
    fontWeight: 'bold',
    padding: theme.spacing(2),
  },

  [`& .${classes.icon}`]: {
    marginLeft: '10px',
    marginRight: '1px',
  },

  [`& .${classes.root}`]: {
    flexGrow: 1,
  },

  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },
}));

const StyledPaper = styled(Paper)<PaperProps>(({ theme }) => ({
  padding: '2px 4px',
  display: 'flex',
  alignItems: 'center',
  width: 380,
  height: 41,
  borderRadius: '100px',
  backgroundColor: '#B0C3C629',
}));

export default function HomeAppBar() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isProvider = !!useLoggedInProviderId();
  const canOnboardUser = useCanOnboardUser();
  const isOnboarding = useIsOnboardingUser();
  const provider = useAppSelector(providerSlice.selectProvider);

  const [userName, setUserName] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const providerID = useLoggedInProviderId();

  React.useEffect(() => {
    if (isProvider && providerID) {
      dispatch(providerSlice.getProviderAsync(providerID));
    }
  }, []);

  React.useEffect(() => {
    if (isProvider && provider) {
      setUserName(provider.transportationProviderName ?? '');
    } else {
      authService.getUserInfo().subscribe((info) => {
        setUserName(info.name);
      });
    }
  }, [provider]);

  const getClassName = (path: string, url: string) => {
    return path.indexOf(url) >= 0 ? classes.selectedLink : classes.link;
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const gotoAbout = () => {
    closeMenu();
    navigate('/about');
  };

  const gotoOnboardUser = () => {
    closeMenu();
    navigate('/onboard');
  };

  const logout = () => {
    closeMenu();
    authService.logout().then(() => navigate('/login'));
  };

  return (
    <StyledAppBar color="transparent" position="sticky">
      <Toolbar className={classes.toolbar}>
        <Typography variant="h5" className={classes.appName}>
          <img
            className={classes.logo}
            src="/logo.png"
            alt="logo"
            data-testid="applogo"
          />
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 4.5,
            alignItems: 'baseline',
          }}>
          <StyledPaper component="form">
            <InputBase
              sx={{
                ml: 3,
                flex: 1,
                fontFamily: 'Source Sans Pro',
                color: 'white',
                fontSize: 16,
                fontWeight: 400,
                '& .MuiInputBase-input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.75)',
                    opacity: 1,
                  },
                },
              }}
              placeholder="Trip, Driver, etc."
            />
            <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
              <Search sx={{ color: 'white' }} />
            </IconButton>
          </StyledPaper>
          <Box
            component="div"
            sx={{
              p: 0,
            }}>
            <IconButton
              onClick={openMenu}
              color="inherit"
              size="large"
              sx={{ p: 0 }}>
              <Avatar src="/avatar.png" />
              <ExpandMore />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={open}
              onClose={closeMenu}>
              {!isOnboarding && (
                <MenuItem onClick={gotoAbout}>{t('app.about')}</MenuItem>
              )}
              {canOnboardUser && (
                <MenuItem onClick={gotoOnboardUser}>
                  {t('app.onboardUser')}
                </MenuItem>
              )}
              <MenuItem onClick={logout}>{t('app.logout')}</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}
